// /economy-summary — consolidated economy overview (staff-only).
// Epic #550 Phase 3, issue #572.
//
// Answers "how is wealth/resources generated vs. where do they sink" across all
// systems over a selectable window. Reads the cron-written economy_daily_summary
// rollup via GET /api/admin/economy-summary.
//
// - Per-resource cards: net generation (transfers excluded) + a net-per-day
//   sparkline + transfer volume.
// - Per-(system, resource) table: sources / sinks / net / events / type.
// - CSV export of the raw per-day rows.
// D-Currency-1: cash formatted with ¥ (U+00A5), credits with ₢ (U+20A2).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@awakenedlands/ui';
import { apiFetch, apiDownload } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';
import { Sparkline } from '@/components/admin/sparkline';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SystemRow {
  system: string;
  resource: string;
  sources: number;
  sinks: number;
  net: number;
  event_count: number;
  is_transfer: number;
}

interface ResourceTotal {
  resource: string;
  sources: number;
  sinks: number;
  net: number;
  transfer_volume: number;
}

interface DailyNetRow {
  day: string;
  resource: string;
  net: number;
}

interface SummaryResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  by_system: SystemRow[];
  by_resource: ResourceTotal[];
  daily: DailyNetRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const RESOURCE_LABEL: Record<string, string> = {
  cash: 'Cash',
  credits: 'Credits',
  ap: 'Action Points',
  energy: 'Energy',
  career_points: 'Career Points',
  gang_points: 'Gang Points',
  xp: 'Experience',
};

function resourceLabel(resource: string): string {
  return RESOURCE_LABEL[resource] ?? resource;
}

/** Format a resource amount; cash/credits get their D-Currency-1 symbols. */
function fmt(resource: string, value: number): string {
  const n = Math.round(value);
  if (resource === 'cash') return `¥${n.toLocaleString()}`;
  if (resource === 'credits') return `₢${n.toLocaleString()}`;
  return n.toLocaleString();
}

function buildQueryString(since: string, until: string, userId: string, extra?: Record<string, string>): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  for (const [k, v] of Object.entries(extra ?? {})) params.set(k, v);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Net-per-day series for one resource, in chronological order. */
function dailySeries(daily: DailyNetRow[], resource: string): number[] {
  return daily.filter((d) => d.resource === resource).map((d) => d.net);
}

// ── Sub-components ──────────────────────────────────────────────────────────────

function ResourceCard({
  total,
  series,
}: {
  total: ResourceTotal;
  series: number[];
}) {
  return (
    <div className="rounded-sm border border-border bg-panel-elev p-4 space-y-2" data-testid={`resource-card-${total.resource}`}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-ink-high">{resourceLabel(total.resource)}</h3>
        <span className="text-[11px] uppercase tracking-wide text-ink-low">net generation</span>
      </div>
      <div className={`text-xl font-mono tabular-nums ${total.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {fmt(total.resource, total.net)}
      </div>
      <div className="text-ink-mid">
        <Sparkline values={series} label={`${resourceLabel(total.resource)} net per day`} />
      </div>
      <dl className="text-xs text-ink-mid space-y-0.5">
        <div className="flex justify-between">
          <dt>Sources</dt>
          <dd className="font-mono tabular-nums">{fmt(total.resource, total.sources)}</dd>
        </div>
        <div className="flex justify-between">
          <dt>Sinks</dt>
          <dd className="font-mono tabular-nums">{fmt(total.resource, total.sinks)}</dd>
        </div>
        {total.transfer_volume > 0 && (
          <div className="flex justify-between text-ink-low">
            <dt>Transfer volume</dt>
            <dd className="font-mono tabular-nums">{fmt(total.resource, total.transfer_volume)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function SystemTable({ rows }: { rows: SystemRow[] }) {
  return (
    <div className="overflow-x-auto rounded-sm border border-border">
      <table className="w-full text-sm" data-testid="economy-system-table">
        <thead className="bg-muted text-left">
          <tr>
            <th className="px-3 py-2 font-medium text-ink-mid">System</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Resource</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Sources</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Sinks</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Net</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Events</th>
            <th className="px-3 py-2 font-medium text-ink-mid">Type</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-divider">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-panel-elev">
              <td className="px-3 py-2 font-mono text-xs">{row.system}</td>
              <td className="px-3 py-2 text-xs">{resourceLabel(row.resource)}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{fmt(row.resource, row.sources)}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{fmt(row.resource, row.sinks)}</td>
              <td className={`px-3 py-2 font-mono text-xs tabular-nums ${row.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmt(row.resource, row.net)}
              </td>
              <td className="px-3 py-2 tabular-nums">{row.event_count.toLocaleString()}</td>
              <td className="px-3 py-2 text-xs text-ink-low">{row.is_transfer ? 'transfer' : 'flow'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function EconomySummaryPage() {
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');
  const [exportError, setExportError] = useState('');

  const { data, isLoading, isError } = useQuery<SummaryResponse>({
    queryKey: ['economy-summary', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<SummaryResponse>(`/admin/economy-summary${qs}`);
      if (!res.ok) throw new Error('Failed to load economy overview.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  async function handleExport() {
    setExportError('');
    // CSV export: only available in all-players mode (userId ignored for CSV per API docs).
    const qs = buildQueryString(appliedSince, appliedUntil, '', { format: 'csv' });
    try {
      await apiDownload(`/admin/economy-summary${qs}`, 'economy-summary.csv');
    } catch {
      setExportError('Export failed.');
    }
  }

  return (
    <EconomyDashboardLayout
      title="Economy Overview"
      description="Consolidated source-vs-sink across every system. Net generation excludes player-to-player transfers (shown separately). Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load economy overview."
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-ink-low">
          Rolled up nightly by <span className="font-mono">daily.economy-summary</span> from the per-system logs.
        </p>
        <Button type="button" variant="secondary" onClick={handleExport} disabled={isLoading}>
          Export CSV
        </Button>
      </div>
      {exportError && <p className="text-sm text-red-600">{exportError}</p>}

      {data != null && (
        <>
          {data.by_resource.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-lg font-medium text-ink-high">Net generation by resource</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {data.by_resource.map((t) => (
                  <ResourceCard key={t.resource} total={t} series={dailySeries(data.daily, t.resource)} />
                ))}
              </div>
            </section>
          ) : (
            <p className="text-sm text-ink-low">No economy data available for this range.</p>
          )}

          {data.by_system.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Per system &amp; resource</h2>
              <SystemTable rows={data.by_system} />
            </section>
          )}
        </>
      )}
    </EconomyDashboardLayout>
  );
}
